import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import LpForm from "@/pages/lp/components/LpForm";
import { PHONE } from "@/mocks/updiag";
import type { DeptData } from "@/mocks/departments";
import { citiesData } from "@/mocks/cities";
import { SHOW_REVIEWS } from "@/config/features";

interface Props {
  dept: DeptData;
}

const diagnosticLinks = [
  { label: "DPE", href: "/diagnostics/dpe", icon: "ri-fire-line" },
  { label: "Pack Vente", href: "/diagnostics/pack-vente", icon: "ri-home-2-line" },
  { label: "Pack Location", href: "/diagnostics/pack-location", icon: "ri-key-2-line" },
  { label: "Amiante", href: "/diagnostics/amiante", icon: "ri-shield-check-line" },
  { label: "Plomb (CREP)", href: "/diagnostics/plomb", icon: "ri-test-tube-line" },
  { label: "Électricité", href: "/diagnostics/electricite", icon: "ri-flashlight-line" },
  { label: "Gaz", href: "/diagnostics/gaz", icon: "ri-gas-station-line" },
  { label: "Loi Carrez", href: "/diagnostics/loi-carrez", icon: "ri-ruler-2-line" },
];

// Mapping arrondissement Paris → groupe (page arrondissement)
// "Paris 1er" / "Paris 2e" ... → slug du groupe
const PARIS_ARR_GROUP: Record<string, string> = {
  "1er": "centre", "2e": "centre", "3e": "centre", "4e": "centre",
  "5e": "rive-gauche", "6e": "rive-gauche", "7e": "rive-gauche",
  "8e": "nord-ouest", "9e": "nord-ouest", "10e": "nord-ouest",
  "11e": "est", "12e": "est",
  "13e": "sud", "14e": "sud", "15e": "sud",
  "16e": "ouest", "17e": "ouest",
  "18e": "nord", "19e": "nord", "20e": "nord",
};

function getCityHref(cityName: string, deptCode: string): string | null {
  // Paris : mapping arrondissement → groupe
  if (deptCode === "75") {
    const match = cityName.match(/Paris\s+(\d+e?r?)/);
    if (match && PARIS_ARR_GROUP[match[1]]) {
      return `/paris/${PARIS_ARR_GROUP[match[1]]}`;
    }
    return null;
  }
  // Autres départements : chercher dans citiesData
  const normalized = cityName
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const city = citiesData.find((c) => c.slug === normalized);
  return city ? `/villes/${city.slug}` : null;
}

export default function DeptPage({ dept }: Props) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    document.title = dept.metaTitle;
    const metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) metaDesc.setAttribute("content", dept.metaDesc);
    window.scrollTo(0, 0);
  }, [dept]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Inter, sans-serif" }}>
      <Header />

      {/* Hero */}
      <section className="relative min-h-[520px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={dept.heroImage}
            alt={`Diagnostic immobilier ${dept.fullName}`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F3B5F]/90 via-[#0F3B5F]/70 to-transparent"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F97316]/20 border border-[#F97316]/40 rounded-full text-[#F97316] text-sm font-semibold mb-6">
                <i className="ri-map-pin-2-fill"></i>
                Département {dept.code} — {dept.region}
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
                {dept.heroTitle}
              </h1>
              <p className="text-lg text-white/80 mb-8 leading-relaxed">
                {dept.heroSubtitle}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                {dept.stats.map((stat) => (
                  <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20">
                    <div className="text-2xl font-extrabold text-[#F97316]" style={{ fontFamily: "Barlow, sans-serif" }}>{stat.value}</div>
                    <div className="text-xs text-white/70 mt-1 leading-tight">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={`tel:${PHONE.replace(/\s/g, "")}`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-[#0F3B5F] rounded-xl font-bold hover:bg-gray-100 transition-all whitespace-nowrap"
                >
                  <i className="ri-phone-fill"></i>
                  {PHONE}
                </a>
                <a
                  href="#devis"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap"
                >
                  <i className="ri-file-list-3-line"></i>
                  Devis gratuit en 2 min
                </a>
              </div>
            </div>

            {/* Right: Form */}
            <div id="devis" className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <LpForm
                preselectedDiag="dpe"
                formTitle={`Devis gratuit — ${dept.fullName}`}
                formSubtitle="Réponse sous 2h — Sans engagement"
                ctaLabel="Obtenir mon devis gratuit"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-[#F97316] transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/zones" className="hover:text-[#F97316] transition-colors">Zones d'intervention</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#0F3B5F] font-medium">{dept.fullName}</span>
          </nav>
        </div>
      </div>

      {/* Intro + Diagnostics rapides */}
      <section className="py-16 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
              Diagnostics immobiliers certifiés dans le {dept.fullName}
            </h2>
            <p className="text-gray-600 leading-relaxed text-base mb-6">{dept.intro}</p>

            {/* Specificities */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              {dept.specificities.map((spec) => (
                <div key={spec.title} className="flex gap-4 p-5 bg-[#F8FAFC] rounded-xl border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0F3B5F]/10 rounded-lg flex-shrink-0">
                    <i className={`${spec.icon} text-[#0F3B5F] text-lg`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-[#0F3B5F] text-sm mb-1">{spec.title}</div>
                    <div className="text-gray-500 text-xs leading-relaxed">{spec.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar: Diagnostics */}
          <div>
            <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 sticky top-24">
              <h3 className="font-extrabold text-[#0F3B5F] text-base mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
                Nos diagnostics dans le {dept.code}
              </h3>
              <div className="flex flex-col gap-2">
                {diagnosticLinks.map((d) => (
                  <Link
                    key={d.href}
                    to={d.href}
                    className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-gray-100 hover:border-[#F97316] hover:text-[#F97316] transition-all text-sm font-medium text-[#334155] cursor-pointer"
                  >
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className={`${d.icon} text-base`}></i>
                    </div>
                    {d.label}
                    <i className="ri-arrow-right-s-line ml-auto"></i>
                  </Link>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <a
                  href={`tel:${PHONE.replace(/\s/g, "")}`}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-[#0F3B5F] text-white rounded-xl font-bold text-sm hover:bg-[#0a2d4a] transition-all whitespace-nowrap"
                >
                  <i className="ri-phone-fill"></i>
                  {PHONE}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ Villes couvertes — refonte visuelle ═══════════ */}
      <section className="relative py-20 overflow-hidden">
        {/* Background dégradé bleu nuit + pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A2B47] via-[#0F3B5F] to-[#1A4A76]"></div>
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        ></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#F97316]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F59E0B]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Heading */}
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 bg-[#F97316]/15 border border-[#F97316]/30 text-[#FDBA74] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4">
              <i className="ri-map-pin-range-line"></i>
              Zone d'intervention
            </span>
            <h2
              className="text-3xl md:text-4xl font-extrabold text-white mb-3"
              style={{ fontFamily: "Barlow, sans-serif" }}
            >
              Communes couvertes dans le {dept.fullName}
            </h2>
            <p className="text-white/70 text-base max-w-2xl mx-auto">
              Nous intervenons dans <strong className="text-white">toutes les communes du département</strong>{" "}
              — délai sous <strong className="text-[#FDBA74]">48h garanti</strong>, rapport certifié le jour même.
            </p>
          </div>

          {/* Banner KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[#F97316]/20 flex items-center justify-center">
                <i className="ri-map-pin-2-fill text-[#FDBA74] text-xl"></i>
              </div>
              <div
                className="text-3xl font-extrabold text-white mb-1"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                {dept.cities.length}
                <span className="text-[#FDBA74]">+</span>
              </div>
              <div className="text-xs text-white/60 uppercase tracking-wider">
                {dept.code === "75" ? "Arrondissements" : "Communes"}
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[#16A34A]/20 flex items-center justify-center">
                <i className="ri-time-line text-green-300 text-xl animate-pulse"></i>
              </div>
              <div
                className="text-3xl font-extrabold text-white mb-1"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                48<span className="text-green-300">h</span>
              </div>
              <div className="text-xs text-white/60 uppercase tracking-wider">Délai garanti</div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
                <i className={`${SHOW_REVIEWS ? "ri-star-fill" : "ri-award-fill"} text-[#FDBA74] text-xl`}></i>
              </div>
              <div
                className="text-3xl font-extrabold text-white mb-1"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                {SHOW_REVIEWS ? <>4.9<span className="text-[#FDBA74]">/5</span></> : <>Cofrac</>}
              </div>
              <div className="text-xs text-white/60 uppercase tracking-wider">
                {SHOW_REVIEWS ? "127 avis Google" : "Certifié officiel"}
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[#F97316]/20 flex items-center justify-center">
                <i className="ri-price-tag-3-line text-[#FDBA74] text-xl"></i>
              </div>
              <div
                className="text-3xl font-extrabold text-white mb-1"
                style={{ fontFamily: "Barlow, sans-serif" }}
              >
                130<span className="text-[#FDBA74]">€</span>
              </div>
              <div className="text-xs text-white/60 uppercase tracking-wider">DPE dès</div>
            </div>
          </div>

          {/* Grille des communes/arrondissements — cards cliquables */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {dept.cities.map((city) => {
              const href = getCityHref(city.name, dept.code);
              const CardEl: any = href ? Link : "div";
              const cardProps: any = href ? { to: href } : {};

              return (
                <CardEl
                  key={city.name}
                  {...cardProps}
                  className={`group flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-sm font-medium transition-all duration-300 ${
                    city.highlight
                      ? "bg-gradient-to-br from-[#F97316] to-[#F59E0B] text-white border-[#F59E0B] shadow-lg shadow-[#F97316]/30"
                      : "bg-white/5 backdrop-blur-sm text-white border-white/15 hover:bg-white hover:text-[#0F3B5F] hover:border-white hover:-translate-y-0.5 hover:shadow-xl"
                  } ${href ? "cursor-pointer" : ""}`}
                >
                  <i
                    className={`ri-map-pin-2-fill text-base flex-shrink-0 ${
                      city.highlight ? "text-white" : "text-[#FDBA74] group-hover:text-[#F97316]"
                    }`}
                  ></i>
                  <div className="min-w-0 flex-1">
                    <div className="leading-tight truncate">{city.name}</div>
                    <div
                      className={`text-[10px] ${
                        city.highlight ? "text-white/80" : "text-white/50 group-hover:text-gray-500"
                      }`}
                    >
                      {city.zip}
                    </div>
                  </div>
                  {href && (
                    <i
                      className={`ri-arrow-right-s-line text-base flex-shrink-0 transition-transform group-hover:translate-x-0.5 ${
                        city.highlight ? "text-white" : "text-white/40 group-hover:text-[#F97316]"
                      }`}
                    ></i>
                  )}
                </CardEl>
              );
            })}
            <div className="flex items-center justify-center px-4 py-3.5 rounded-xl border-2 border-dashed border-white/20 text-sm text-white/50">
              + toutes les communes
            </div>
          </div>

          {/* Badge chrono flottant + CTA */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="flex items-center gap-3 bg-[#16A34A]/15 border border-green-300/30 backdrop-blur-sm rounded-full px-5 py-3">
              <div className="relative">
                <i className="ri-shield-check-fill text-green-300 text-2xl"></i>
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-400 rounded-full animate-ping"></span>
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-400 rounded-full"></span>
              </div>
              <div>
                <div className="text-xs text-white/60 uppercase tracking-wider">Intervention</div>
                <div className="text-sm font-bold text-white">Garantie sous 48h</div>
              </div>
            </div>
            <Link
              to="/devis"
              className="inline-flex items-center gap-2 px-7 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap shadow-lg shadow-[#F97316]/30"
            >
              <i className="ri-file-list-3-line"></i>
              Obtenir mon devis gratuit
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Contexte local */}
      <section className="py-16 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
              {dept.localContext}
            </h2>
            <p className="text-gray-600 leading-relaxed text-base mb-6">{dept.localContextDetail}</p>

            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-information-line text-amber-600 text-lg"></i>
                </div>
                <p className="text-sm text-amber-800">
                  <strong>Rappel :</strong> Depuis le 1er janvier 2025, les logements classés G sont interdits à la location dans toute l'Île-de-France.
                </p>
              </div>
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-shield-check-line text-green-600 text-lg"></i>
                </div>
                <p className="text-sm text-green-800">
                  <strong>Certifié COFRAC :</strong> Tous nos diagnostiqueurs sont certifiés et accrédités. Nos rapports sont acceptés par tous les notaires et agences immobilières.
                </p>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="rounded-2xl overflow-hidden border border-gray-100 h-80">
            <iframe
              src={dept.mapEmbed}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={`Carte ${dept.fullName}`}
            ></iframe>
          </div>
        </div>
      </section>

      {/* Tarifs */}
      <section className="py-16 bg-[#0F3B5F]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-white mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>
              Nos tarifs dans le {dept.fullName}
            </h2>
            <p className="text-white/70 text-base">Déplacement inclus — Devis gratuit et sans engagement</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {dept.prices.map((price) => (
              <div
                key={price.label}
                className={`rounded-2xl p-6 text-center ${
                  price.highlight
                    ? "bg-[#F97316] text-white"
                    : "bg-white/10 text-white border border-white/20"
                }`}
              >
                <div className="text-2xl font-extrabold mb-2" style={{ fontFamily: "Barlow, sans-serif" }}>{price.price}</div>
                <div className={`text-sm ${price.highlight ? "text-white/90" : "text-white/70"}`}>{price.label}</div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <a
              href="#devis"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0F3B5F] rounded-xl font-extrabold text-base hover:bg-gray-100 transition-all whitespace-nowrap"
            >
              <i className="ri-file-list-3-line"></i>
              Obtenir mon devis personnalisé
            </a>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      {SHOW_REVIEWS && (
        <section className="py-16 max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>
              Avis clients dans le {dept.fullName}
            </h2>
            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-amber-400 text-base"></i>)}
              </div>
              <span>4.9/5 — Clients vérifiés Google</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {dept.testimonials.map((t) => (
              <div key={t.name} className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#0F3B5F] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-[#0F3B5F] text-sm">{t.name}</div>
                    <div className="text-xs text-gray-400">{t.city} — {t.service}</div>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>
                    ))}
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed italic">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-3 flex items-center gap-1 text-xs text-gray-400">
                  <i className="ri-google-fill text-blue-500"></i>
                  Avis Google vérifié
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>
              Questions fréquentes — {dept.fullName}
            </h2>
          </div>

          <div className="flex flex-col gap-3">
            {dept.faq.map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer"
                >
                  <span className="font-semibold text-[#0F3B5F] text-sm pr-4">{item.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-arrow-down-s-line text-[#F97316] text-lg transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-gray-600 text-sm leading-relaxed border-t border-gray-50 pt-4">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Départements voisins */}
      <section className="py-16 max-w-7xl mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-extrabold text-[#0F3B5F] mb-2" style={{ fontFamily: "Barlow, sans-serif" }}>
            Nous intervenons aussi dans les départements voisins
          </h2>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          {dept.nearbyDepts.map((d) => (
            <Link
              key={d.slug}
              to={`/zones/${d.slug}`}
              className="flex items-center gap-3 px-6 py-4 bg-[#F8FAFC] rounded-xl border border-gray-100 hover:border-[#F97316] hover:text-[#F97316] transition-all text-[#334155] font-semibold text-sm cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-[#0F3B5F] text-white rounded-lg text-xs font-bold">
                {d.code}
              </div>
              {d.name}
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          ))}
          <Link
            to="/zones"
            className="flex items-center gap-3 px-6 py-4 bg-[#F97316] text-white rounded-xl font-semibold text-sm hover:bg-orange-600 transition-all cursor-pointer"
          >
            <i className="ri-map-2-line"></i>
            Toutes les zones IDF
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-r from-[#0F3B5F] to-[#1a5a8a]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
            Besoin d'un diagnostic dans le {dept.fullName} ?
          </h2>
          <p className="text-white/80 text-base mb-8">
            Devis gratuit en 2 minutes — Intervention sous 48h — Rapport le jour même
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#devis"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#F97316] text-white rounded-xl font-extrabold text-base hover:bg-orange-600 transition-all whitespace-nowrap"
            >
              <i className="ri-file-list-3-line"></i>
              Devis gratuit en ligne
            </a>
            <a
              href={`tel:${PHONE.replace(/\s/g, "")}`}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#0F3B5F] rounded-xl font-extrabold text-base hover:bg-gray-100 transition-all whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              {PHONE}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
